<template>
  <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90">
    <div class="bg-gray-800 border-2 border-yellow-500 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
      <div class="text-center mb-6">
        <i class="fas fa-box-open text-5xl text-yellow-400 mb-4"></i>
        <h2 class="text-2xl font-bold text-white mb-2">⚠️ ตรวจพบว่าคุณยังใช้งานระบบเก่าอยู่</h2>
        <p class="text-gray-300">
          {{ displayMessage }}
        </p>
      </div>

      <div v-if="isMigrating" class="text-center py-4">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
        <p class="text-white">กำลังย้ายข้อมูลไอเทมของคุณ...</p>
        <p class="text-sm text-gray-400 mt-2">{{ progressText }}</p>

        <!-- Warning Message -->
        <div class="bg-red-900/60 border border-red-500 rounded-lg p-3 mt-4 mb-2">
          <p class="text-red-200 text-sm font-semibold">
            <i class="fas fa-exclamation-circle mr-1"></i>
            ระหว่างย้ายไปยังระบบใหม่ ห้ามกดปิดหรือ Refresh ห้ามเปลี่ยนหน้าจอ
          </p>
          <p class="text-red-300 text-xs mt-1">
            เด็ดขาดจนกว่าจะทำสำเร็จ กรุณารอ 5-10 นาที
          </p>
        </div>

        <!-- Indeterminate Progress Bar -->
        <div class="w-full bg-gray-700 rounded-full h-3 mt-4 overflow-hidden relative">
          <div class="absolute top-0 left-0 h-3 rounded-full bg-gradient-to-r from-yellow-500 via-yellow-300 to-yellow-500 w-1/2 animate-shimmer"></div>
        </div>
      </div>

      <div v-else-if="migrateError" class="text-center py-4">
        <i class="fas fa-exclamation-triangle text-red-500 text-3xl mb-2"></i>
        <p class="text-red-400">{{ migrateError }}</p>
        <button
          @click="retryMigrate"
          class="mt-4 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
        >
          ลองอีกครั้ง
        </button>
      </div>

      <div v-else class="space-y-4">
        <div class="bg-gray-700 rounded-lg p-4 text-left">
          <p class="text-gray-300 text-sm">
            ระบบใหม่จะทำให้การทำงานมีประสิทธิภาพมากขึ้นและเร็วมากขึ้นกว่าเดิม
          </p>
        </div>

        <button
          @click="startMigrate"
          class="w-full px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-bold rounded-lg shadow-lg transition-colors flex items-center justify-center gap-2"
        >
          <i class="fas fa-magic"></i>
          ย้ายไปยังระบบใหม่
        </button>

        <p class="text-xs text-gray-500 text-center">
          หากพบปัญหา กรุณาติดต่อแอดมิน
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { projectFunctions } from '@/firebase/config';

const props = defineProps({
  userId: { type: String, required: true },
  forceMessage: { type: String, default: '' }
});

const emit = defineEmits(['migrated']);

const isMigrating = ref(false);
const progress = ref(0);
const progressText = ref('กำลังเตรียมข้อมูล...');
const migrateError = ref('');

const defaultMessage = 'ระบบไอเทมของคุณต้องถูกย้ายไปยังระบบใหม่ที่มีประสิทธิภาพกว่า';
const reMigrationMessage = 'กรุณาย้ายระบบเพื่อใช้งานต่อ';

const displayMessage = computed(() => {
  if (props.forceMessage) return props.forceMessage;
  return reMigrationMessage;
});

const startMigrate = async () => {
  isMigrating.value = true;
  progress.value = 0;
  migrateError.value = '';

  try {
    progress.value = 20;
    progressText.value = 'กำลังอ่านข้อมูลไอเทมเดิม...';

    const gameActions = projectFunctions.httpsCallable('gameActions');
    
    progress.value = 50;
    progressText.value = 'กำลังย้ายไอเทมเข้าระบบใหม่...';

    const result = await gameActions({ action: 'migrateItems' });

    progress.value = 100;
    progressText.value = 'ย้ายสำเร็จ! กำลังโหลดข้อมูลใหม่...';

    // Wait a bit then emit
    setTimeout(() => {
      emit('migrated');
    }, 800);

  } catch (err) {
    console.error('Migrate error:', err);
    migrateError.value = err.message || 'เกิดข้อผิดพลาดในการย้ายข้อมูล';
    isMigrating.value = false;
  }
};

const retryMigrate = () => {
  migrateError.value = '';
  startMigrate();
};
</script>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-200%);
  }
  100% {
    transform: translateX(200%);
  }
}

.animate-shimmer {
  animation: shimmer 1.5s ease-in-out infinite;
}
</style>
